import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {
    ControlEventMessage,
    IEventMessage,
    TopologyStatus
} from '@mintdata-wsp/common/controllers/flow/FlowCloudController';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    FlowSimulation,
    SimulationId,
    SimulationMessage,
    SimulationStatus,
    SimulationStopReason
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action} from 'mobx';

export class FlowSimulationController
{
    @action
    public add(simulation:FlowSimulation)
    {
        const simulations = this._getState();

        simulations.push(simulation);
    }

    @action
    public clearAll():void
    {
        rootState.ui.visualDesigner.flow.flowSimulations = [];
    }

    @action
    public remove(predicate:(simulation:FlowSimulation) => boolean):FlowSimulation[]
    {
        const {flow} = rootState.ui.visualDesigner;

        const removed = [];

        flow.flowSimulations = flow.flowSimulations.filter((simulation) =>
        {
            const shouldRemove = predicate(simulation);

            if( shouldRemove )
            {
                removed.push(simulation);
            }

            return !shouldRemove;
        });

        return removed;
    }

    public getAll():FlowSimulation[]
    {
        return this._getState();
    }

    public getById(id:SimulationId):FlowSimulation
    {
        return this._getState().find((item) => item.id === id);
    }

    public getForFlowEditor(flowId:number):FlowSimulation
    {
        const simulations = this._getState();

        return simulations.find((item) => item.functionId === flowId && item.slot === null);
    }

    public getForSlot(slotId:SlotId):FlowSimulation
    {
        const simulations = this._getState();

        return simulations.find((item) => item.slot && item.slot.id === slotId);
    }

    public hasMessages(id:SimulationId):boolean
    {
        return this.getMessages(id).length > 0;
    }

    public getMessages(id:SimulationId):SimulationMessage[]
    {
        return this.getById(id).messages;
    }

    public shouldSkipCloudMessage(id:SimulationId, message:IEventMessage):boolean
    {
        const simulation = this.getById(id);
        if( !simulation )
            return true;

        if( this.isStoppedOrInitialNotRunning(id) )
        {
            const isTerminationMessage = message instanceof ControlEventMessage
                && (message.state === TopologyStatus.Stopping || message.state === TopologyStatus.Terminated);

            // don't show warnings for termination states as they might come with a lag
            if( !isTerminationMessage )
            {
                console.warn(
                    `Warning: Cloud message has been received for a stopped simulation.
                    Simulation id: ${id}.`
                );
            }

            return true;
        }

        //TODO: this check seems overcomplicated, can we simplify it somehow?
        const openDesign = commonControllers.designState.getOpenDesign();
        if( simulation.isForSlot && openDesign && openDesign.designRef && openDesign.designRef.designId )
        {
            const flow = commonControllers.flow.getFlowById(simulation.functionId);
            if( flow && flow.designId !== openDesign.designRef.designId )
            {
                console.warn(
                    `Warning: Cloud message has been received for a slot of a closed design.
                     Current open design id: ${openDesign ? openDesign.designRef.designId : null}.
                     Message has been received for design id: ${flow.designId}.
                     Simulation id: ${id}.`
                );

                return true;
            }
        }

        return false;
    }

    @action
    public clearMessages(id:SimulationId):void
    {
        this.getById(id).messages = [];
    }

    @action
    public setMessages(id:SimulationId, messages:SimulationMessage[]):void
    {
        this.getById(id).messages = messages;
    }

    @action
    public addMessage(id:SimulationId, message:SimulationMessage):void
    {
        this.getMessages(id).push(message);
    }

    @action.bound
    public setSimulationStatusToStopped(id:SimulationId, stopReason:SimulationStopReason):void
    {
        const simulation = this.getById(id);

        simulation.stopReason = stopReason;
        simulation.status = SimulationStatus.Stopped;
    }

    @action.bound
    public setSimulationStatus(id:SimulationId, status:SimulationStatus):void
    {
        if( status === SimulationStatus.Stopped )
        {
            console.error('Error: cannot set Stopped status via this method; use setSimulationStatusToStopped() instead.');
            return;
        }

        const simulation = this.getById(id);
        simulation.status = status;
    }

    public isStoppedOrInitialNotRunning = (id:SimulationId) =>
    {
        const status = this.getSimulationStatus(id);

        return status == SimulationStatus.Stopped ||
            status == SimulationStatus.InitialNotRunning;
    };

    public isRunningOrStarting(id:SimulationId):boolean
    {
        return this.isRunning(id) || this.isStarting(id);
    }

    public isRunning(id:SimulationId):boolean
    {
        const status = this.getSimulationStatus(id);

        return status === SimulationStatus.Running ||
            status === SimulationStatus.WaitingForFirstVisualData;
    }

    public isStarting(id:SimulationId):boolean
    {
        return this.getSimulationStatus(id) === SimulationStatus.Starting;
    }

    public isStopping(id:SimulationId):boolean
    {
        return this.getSimulationStatus(id) === SimulationStatus.Stopping;
    }

    public isEverHadData(id:SimulationId):boolean
    {
        return this.getById(id).isEverHadData;
    }

    @action
    public markSawData(id:SimulationId):void
    {
        this.getById(id).isEverHadData = true;
    }

    public getSimulationStatus(id:SimulationId):SimulationStatus
    {
        return this.getById(id).status;
    }

    public getSimulationStopReason(id:SimulationId):SimulationStopReason
    {
        return this.getById(id).stopReason;
    }

    private _getState():FlowSimulation[]
    {
        return rootState.ui.visualDesigner.flow.flowSimulations;
    }
}
